/**
 * Dashboard canvas: a twelve-column grid of widgets with an edit mode for
 * dragging, resizing, configuring and adding.
 *
 * Layout changes go through React and localStorage; nothing here touches live
 * values, which the widgets read for themselves.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ObdSession, SignalState } from '@obd/core';
import {
  FAST_INTERVAL_CHOICES, WIDGET_LABELS, materialisePortrait, resolveLayout,
  type Dashboard, type Geom, type Widget, type WidgetScope, type WidgetType, uid,
} from '../store/appState';
import { WIDGET_COMPONENTS } from './widgets';
import { MAX_SERIES } from './render';
import { SignalPicker } from './SignalPicker';

const DEFAULT_ROW_HEIGHT = 68;
const MIN_ROW_HEIGHT = 34;
/** A fitted row may be at most this many times a column's width. */
const MAX_ROW_ASPECT = 1.5;
const GAP = 10;

export interface DashboardViewProps {
  dashboard: Dashboard;
  session: ObdSession;
  editing: boolean;
  onChange: (next: Dashboard) => void;
}

/**
 * Decide whether to use the portrait arrangement.
 *
 * Driven by the measured container rather than a media query, because what
 * matters is the shape of the space the grid actually has. A phone held
 * upright and a narrow desktop window both want the stacked layout.
 */
function useNarrowLayout(containerRef: React.RefObject<HTMLDivElement | null>): boolean {
  const [narrow, setNarrow] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = (): void => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w === 0) return;
      // Clearly taller than wide, or simply too few pixels across for a
      // twelve-column grid to leave anything legible in one column.
      setNarrow(h > w * 1.05 || w < 560);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('orientationchange', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', measure);
    };
  }, [containerRef]);

  return narrow;
}

/**
 * Row height for a fit-to-screen dashboard.
 *
 * Divides the available height by however many rows the layout occupies, so
 * the whole thing lands on one screen with nothing to scroll to -- which is
 * the only usable arrangement for a phone in a windscreen stand.
 */
function useRowHeight(
  fit: boolean,
  rows: number,
  columns: number,
  containerRef: React.RefObject<HTMLDivElement | null>,
): number {
  const [rowHeight, setRowHeight] = useState(DEFAULT_ROW_HEIGHT);

  useEffect(() => {
    if (!fit || rows === 0) {
      setRowHeight(DEFAULT_ROW_HEIGHT);
      return;
    }
    const el = containerRef.current;
    if (!el) return;

    const measure = (): void => {
      const available = el.clientHeight - GAP * (rows - 1);
      const colWidth = (el.clientWidth - GAP * (columns - 1)) / columns;
      // Cap how tall a row may stretch relative to a column. Without this a
      // short dashboard on a tall screen gets rows twice their width, which
      // turns every round gauge into an egg and wastes the space anyway.
      const ceiling = colWidth > 0 ? colWidth * MAX_ROW_ASPECT : Infinity;
      const ideal = available / rows;
      setRowHeight(Math.max(MIN_ROW_HEIGHT, Math.floor(Math.min(ideal, ceiling))));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('orientationchange', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', measure);
    };
  }, [fit, rows, columns, containerRef]);

  return fit ? rowHeight : DEFAULT_ROW_HEIGHT;
}

interface DragState {
  id: string;
  mode: 'move' | 'resize';
  startX: number;
  startY: number;
  origin: { x: number; y: number; w: number; h: number };
  colWidth: number;
}

export function DashboardView({ dashboard, session, editing, onChange }: DashboardViewProps): JSX.Element {
  const gridRef = useRef<HTMLDivElement | null>(null);
  const outerRef = useRef<HTMLDivElement | null>(null);
  const narrow = useNarrowLayout(outerRef);
  const layout = useMemo(() => resolveLayout(dashboard, narrow), [dashboard, narrow]);
  const rows = layout.widgets.reduce((max, w) => Math.max(max, w.y + w.h), 0);
  const rowHeight = useRowHeight(dashboard.fit ?? false, rows, layout.columns, outerRef);
  const dragRef = useRef<DragState | null>(null);
  const [, forceRender] = useState(0);
  const [configuring, setConfiguring] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const rowHeightRef = useRef(rowHeight);
  rowHeightRef.current = rowHeight;

  /**
   * Apply a change to the right place.
   *
   * Geometry is per-orientation, so moving a widget in portrait must not
   * disturb the landscape arrangement (and vice versa). Everything else --
   * title, poll rate, ranges -- belongs to the widget itself and is shared.
   */
  const updateWidget = useCallback(
    (id: string, patch: Partial<Widget>) => {
      const { x, y, w, h, ...rest } = patch;
      const geomPatch: Partial<Geom> = {};
      if (x !== undefined) geomPatch.x = x;
      if (y !== undefined) geomPatch.y = y;
      if (w !== undefined) geomPatch.w = w;
      if (h !== undefined) geomPatch.h = h;
      const hasGeom = Object.keys(geomPatch).length > 0;

      let next: Dashboard = dashboard;

      if (hasGeom && narrow) {
        // First portrait edit materialises the derived layout so it becomes
        // the user's own, rather than being regenerated over their change.
        const base = dashboard.portrait ?? materialisePortrait(dashboard);
        const current = base[id];
        if (current) {
          next = { ...next, portrait: { ...base, [id]: { ...current, ...geomPatch } } };
        }
      } else if (hasGeom) {
        next = {
          ...next,
          widgets: next.widgets.map((wd) => (wd.id === id ? { ...wd, ...geomPatch } : wd)),
        };
      }

      if (Object.keys(rest).length > 0) {
        next = {
          ...next,
          widgets: next.widgets.map((wd) => (wd.id === id ? { ...wd, ...rest } : wd)),
        };
      }

      onChange(next);
    },
    [dashboard, narrow, onChange],
  );

  const removeWidget = useCallback(
    (id: string) => {
      const portrait = dashboard.portrait ? { ...dashboard.portrait } : undefined;
      if (portrait) delete portrait[id];
      onChange({
        ...dashboard,
        widgets: dashboard.widgets.filter((w) => w.id !== id),
        ...(portrait ? { portrait } : {}),
      });
      setConfiguring((c) => (c === id ? null : c));
    },
    [dashboard, onChange],
  );

  const beginDrag = (e: React.PointerEvent, widget: Widget, mode: DragState['mode']): void => {
    if (!editing) return;
    e.preventDefault();
    e.stopPropagation();
    const grid = gridRef.current;
    if (!grid) return;
    const colWidth = (grid.clientWidth - GAP * (layout.columns - 1)) / layout.columns;
    rowHeightRef.current = rowHeight;
    dragRef.current = {
      id: widget.id,
      mode,
      startX: e.clientX,
      startY: e.clientY,
      origin: { x: widget.x, y: widget.y, w: widget.w, h: widget.h },
      colWidth,
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent): void => {
    const drag = dragRef.current;
    if (!drag) return;
    const dxCells = Math.round((e.clientX - drag.startX) / (drag.colWidth + GAP));
    const dyCells = Math.round((e.clientY - drag.startY) / (rowHeightRef.current + GAP));
    const w = layout.widgets.find((x) => x.id === drag.id);
    if (!w) return;

    if (drag.mode === 'move') {
      const nx = clamp(drag.origin.x + dxCells, 0, layout.columns - w.w);
      const ny = Math.max(0, drag.origin.y + dyCells);
      if (nx !== w.x || ny !== w.y) updateWidget(drag.id, { x: nx, y: ny });
    } else {
      const nw = clamp(drag.origin.w + dxCells, 1, layout.columns - w.x);
      const nh = Math.max(1, drag.origin.h + dyCells);
      if (nw !== w.w || nh !== w.h) updateWidget(drag.id, { w: nw, h: nh });
    }
    forceRender((v) => v + 1);
  };

  const endDrag = (): void => {
    dragRef.current = null;
  };

  const addWidget = (type: WidgetType, signals: string[]): void => {
    const y = dashboard.widgets.reduce((max, w) => Math.max(max, w.y + w.h), 0);
    const size = DEFAULT_SIZES[type];
    onChange({
      ...dashboard,
      widgets: [
        ...dashboard.widgets,
        { id: uid(), type, signals, x: 0, y, w: size.w, h: size.h, intervalMs: 250, sparkline: type === 'number' },
      ],
    });
    setAdding(false);
  };

  const configured = layout.widgets.find((w) => w.id === configuring);

  return (
    <div className={'dashboard' + (dashboard.fit ? ' is-fit' : '')} ref={outerRef}>
      <div
        className="grid"
        ref={gridRef}
        style={{
          gridTemplateColumns: `repeat(${layout.columns}, minmax(0, 1fr))`,
          gridAutoRows: `${rowHeight}px`,
          gap: GAP,
        }}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {layout.widgets.map((widget) => {
          const Component = WIDGET_COMPONENTS[widget.type];
          return (
            <div
              key={widget.id}
              className="widget"
              data-editing={editing || undefined}
              style={{
                gridColumn: `${widget.x + 1} / span ${widget.w}`,
                gridRow: `${widget.y + 1} / span ${widget.h}`,
              }}
            >
              {editing && (
                <div className="widget-tools">
                  <button
                    className="widget-grip"
                    title="Drag to move"
                    onPointerDown={(e) => beginDrag(e, widget, 'move')}
                  >
                    ⠿
                  </button>
                  <button className="widget-tool" title="Configure" onClick={() => setConfiguring(widget.id)}>
                    ⚙
                  </button>
                  <button className="widget-tool" title="Remove" onClick={() => removeWidget(widget.id)}>
                    ✕
                  </button>
                </div>
              )}
              <div className="widget-body">
                <Component widget={widget} session={session} />
              </div>
              {editing && (
                <div
                  className="widget-resize"
                  title="Drag to resize"
                  onPointerDown={(e) => beginDrag(e, widget, 'resize')}
                />
              )}
            </div>
          );
        })}
      </div>

      {editing && (
        <div className="dashboard-actions">
          <button className="btn" data-variant="primary" onClick={() => setAdding(true)}>
            + Add widget
          </button>
          <span className="muted">Drag ⠿ to move, the corner to resize.</span>
          {narrow && (
            <>
              {/* Position is per-orientation, so say which one is being edited. */}
              <span className="pill">
                {dashboard.portrait ? 'editing portrait layout' : 'portrait — auto-arranged'}
              </span>
              {dashboard.portrait && (
                <button
                  className="btn"
                  data-size="sm"
                  onClick={() => {
                    const { portrait: _discarded, ...rest } = dashboard;
                    onChange(rest);
                  }}
                >
                  Re-arrange automatically
                </button>
              )}
            </>
          )}
        </div>
      )}

      {adding && <AddWidgetDialog session={session} onAdd={addWidget} onClose={() => setAdding(false)} />}
      {configured && (
        <ConfigureDialog
          widget={configured}
          session={session}
          onChange={(patch) => updateWidget(configured.id, patch)}
          onClose={() => setConfiguring(null)}
        />
      )}
    </div>
  );
}

const DEFAULT_SIZES: Record<WidgetType, { w: number; h: number }> = {
  gauge: { w: 4, h: 4 },
  bar: { w: 4, h: 1 },
  number: { w: 3, h: 2 },
  line: { w: 12, h: 4 },
  light: { w: 3, h: 1 },
  score: { w: 3, h: 4 },
  gear: { w: 2, h: 2 },
  shift: { w: 3, h: 2 },
};

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

// ------------------------------------------------------------------ dialogs

function AddWidgetDialog({
  session, onAdd, onClose,
}: {
  session: ObdSession;
  onAdd: (type: WidgetType, signals: string[]) => void;
  onClose: () => void;
}): JSX.Element {
  const [type, setType] = useState<WidgetType>('gauge');
  const [selected, setSelected] = useState<string[]>([]);
  const multi = type === 'line';

  const toggle = (key: string): void => {
    setSelected((prev) => {
      if (!multi) return [key];
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (prev.length >= MAX_SERIES) return prev;
      return [...prev, key];
    });
  };

  return (
    <Modal title="Add widget" onClose={onClose}>
      <label className="field">
        <span>Widget type</span>
        <select value={type} onChange={(e) => { setType(e.target.value as WidgetType); setSelected([]); }}>
          {Object.entries(WIDGET_LABELS).map(([k, label]) => (
            <option key={k} value={k}>{label}</option>
          ))}
        </select>
      </label>
      <p className="muted" style={{ margin: '4px 0 10px' }}>
        {multi
          ? `Pick up to ${MAX_SERIES} signals. Each gets its own stacked panel sharing the time axis.`
          : 'Pick the signal this widget shows.'}
      </p>
      <SignalPicker session={session} selected={selected} onToggle={toggle} />
      <div className="modal-actions">
        <button className="btn" onClick={onClose}>Cancel</button>
        <button
          className="btn"
          data-variant="primary"
          disabled={selected.length === 0}
          onClick={() => onAdd(type, selected)}
        >
          Add
        </button>
      </div>
    </Modal>
  );
}

function ConfigureDialog({
  widget, session, onChange, onClose,
}: {
  widget: Widget;
  session: ObdSession;
  onChange: (patch: Partial<Widget>) => void;
  onClose: () => void;
}): JSX.Element {
  const states = widget.signals
    .map((k) => session.store.get(k))
    .filter((s): s is SignalState => s !== undefined);
  const def = states[0]?.def;

  return (
    <Modal title={'Configure — ' + (def?.name ?? widget.type)} onClose={onClose}>
      <label className="field">
        <span>Title</span>
        <input
          type="text"
          value={widget.title ?? ''}
          placeholder={def?.name ?? ''}
          onChange={(e) => onChange({ title: e.target.value || undefined })}
        />
      </label>

      <label className="field">
        <span>Update rate</span>
        <select
          value={widget.intervalMs ?? 250}
          onChange={(e) => onChange({ intervalMs: Number(e.target.value) })}
        >
          {FAST_INTERVAL_CHOICES.map((c) => (
            <option key={c.ms} value={c.ms}>{c.label}</option>
          ))}
        </select>
      </label>

      {(widget.type === 'gauge' || widget.type === 'bar') && def && (
        <div className="field-row">
          <label className="field">
            <span>Minimum</span>
            <input
              type="number"
              value={widget.min ?? def.min}
              onChange={(e) => onChange({ min: Number(e.target.value) })}
            />
          </label>
          <label className="field">
            <span>Maximum</span>
            <input
              type="number"
              value={widget.max ?? def.max}
              onChange={(e) => onChange({ max: Number(e.target.value) })}
            />
          </label>
        </div>
      )}

      {widget.type === 'line' && (
        <label className="field">
          <span>Time window</span>
          <select value={widget.windowS ?? 90} onChange={(e) => onChange({ windowS: Number(e.target.value) })}>
            {[30, 60, 90, 180, 300, 600, 1800].map((s) => (
              <option key={s} value={s}>{s < 60 ? s + ' s' : s / 60 + ' min'}</option>
            ))}
          </select>
        </label>
      )}

      {(widget.type === 'number' || widget.type === 'gauge' || widget.type === 'bar') && (
        <label className="field">
          <span>Show</span>
          <select
            value={widget.scope ?? 'now'}
            onChange={(e) => onChange({ scope: e.target.value as WidgetScope })}
          >
            <option value="now">Live reading</option>
            <option value="trip">Average this trip</option>
            <option value="tank">Average since fill-up</option>
            <option value="life">Average all time</option>
          </select>
        </label>
      )}

      {widget.type === 'number' && (widget.scope ?? 'now') === 'now' && (
        <label className="field field-inline">
          <input
            type="checkbox"
            checked={widget.sparkline ?? false}
            onChange={(e) => onChange({ sparkline: e.target.checked })}
          />
          <span>Show history trace</span>
        </label>
      )}

      {def?.description && <p className="muted small">{def.description}</p>}

      <div className="modal-actions">
        <button className="btn" data-variant="primary" onClick={onClose}>Done</button>
      </div>
    </Modal>
  );
}

export function Modal({
  title, children, onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}): JSX.Element {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={title}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="btn" data-size="sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

/** Shared by the add dialog and the signals tab. */
export function useAvailableSignals(session: ObdSession, version: number): SignalState[] {
  return useMemo(() => {
    void version;
    return session.store
      .list()
      .filter((s) => s.available || s.def.category === 'derived' || s.def.category === 'trip')
      .sort((a, b) => a.def.category.localeCompare(b.def.category) || a.def.name.localeCompare(b.def.name));
  }, [session, version]);
}
